// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PostalCode from "@bg/App/Components/PostalCode";
import loadSystemList from "@bg/App/Handlers/CommonHandlers/LoadSystemList";
import { CustomAppState } from "@bg/App/Store/Reducers";
import { enablePostalCodeLocationFinder } from "@bg/App/Store/Selectors/PostalCodeSelector";
import Tooltip, { TooltipPresentationProps } from "@bg/MobiusOverrides/Tooltip/Tooltip";
import { constants } from "@bg/XDL/constants";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import { hasLocalStorage } from "@bg/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import { emptyGuid } from "@insite/client-framework/Common/StringHelpers";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCategoryState } from "@insite/client-framework/Store/Data/Categories/CategoriesSelectors";
import loadCategory from "@insite/client-framework/Store/Data/Categories/Handlers/LoadCategory";
import {
    getPageLinkByNodeId,
    getPageLinkByPageType,
    LinkModel,
    useGetLinks,
} from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => ({
    locationsPage: getPageLinkByPageType(state, "LocationFinderPage"),
    isAuth: getSession(state).isAuthenticated,
    session: getSession(state),
    enablePostalCodeLocationFinder: enablePostalCodeLocationFinder(state),
});

const mapDispatchToProps = {
    loadSystemList,
};

const enum fields {
    links = "links",
    showPostalPOpup = "showPostalCodePopup",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: { [fields.links]: LinkModel[]; [fields.showPostalPOpup]: boolean; [fields.customCSS]: string };
    extendedStyles?: HeaderLinkListStyles;
}

export interface HeaderLinkListStyles {
    headerLinkListWrapper?: InjectableCss;
    linkWrapper?: InjectableCss;
    link?: LinkPresentationProps;
    postalTippy?: TooltipPresentationProps;
    logoMark?: LazyImageProps;
}

export const headerLinkListStyles: HeaderLinkListStyles = {
    headerLinkListWrapper: {
        css: css`
            display: flex;
            justify-content: flex-end;
            align-items: center;
        `,
    },
    link: {
        typographyProps: {
            css: css`
                &&& {
                    color: ${getColor("common.white")};
                    font-size: ${fontConfig.fontSize.xSmall}px;
                    font-weight: ${fontConfig.weight.semiBold};
                    letter-spacing: 0;
                    line-height: ${fontConfig.lineHeight.singleLineSmall};
                    text-transform: capitalize;
                }
            `,
        },
        css: css`
            color: ${getColor("common.white")};
        `,
    },
    linkWrapper: {
        css: css`
            margin-right: 15px;
            display: flex;
            align-items: center;
            &:after {
                content: "|";
                color: ${getColor("common.white")};
                margin-left: 15px;
            }
            &:last-child {
                padding-right: 0;
                margin-right: 0;

                &:after {
                    content: none;
                }
            }
        `,
    },
    postalTippy: {
        cssOverrides: {
            tooltipContainer: css`
                max-width: 400px;
            `,
        },
    },
    logoMark: {
        css: css`
            margin-right: 6px;
            img {
                width: 22px;
                height: 22px;
            }
        `,
    },
};

const defaultCustomCss = `.list-wrapper {
}

.list-link {
}
`;

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const HeaderLinkList: React.FC<Props> = ({
    fields,
    extendedStyles,
    locationsPage,
    isAuth,
    loadSystemList,
    session,
    enablePostalCodeLocationFinder,
}) => {
    const links = useGetLinks(fields.links, o => o.fields.destination);
    const [styles] = React.useState(() => mergeToNew(headerLinkListStyles, extendedStyles));

    if (!links || !links.length) {
        return null;
    }

    React.useEffect(() => {
        if (enablePostalCodeLocationFinder) {
            loadSystemList({
                systemListName: "AcceptedDeliveryPostalCodes",
                onSuccess: result => {
                    if (!isAuth && !hasLocalStorage(constants.localStorage.POSTAL_CODE_ID)) {
                        setTimeout(() => {
                            const tippyEl = document.querySelector(".header-locations-link > span");
                            if (tippyEl) {
                                (tippyEl as any)._tippy.show();
                            }
                        }, 100);
                    }
                },
            });
        }
    }, []);

    const customCssWrapper = {
        css: css`
            ${fields.customCSS}
        `,
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapper className="list-wrapper" {...styles.headerLinkListWrapper}>
                {links.map(
                    // eslint-disable-next-line array-callback-return
                    (link, index) => {
                        if (link.url) {
                            if (
                                enablePostalCodeLocationFinder &&
                                (link as PageLinkModel).type === locationsPage?.type &&
                                fields.showPostalCodePopup &&
                                !isAuth
                            ) {
                                const trigger = (
                                    <Link
                                        className="list-link"
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${link.url}.${link.title}${index}`}
                                        href={link.url}
                                        target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                        {...styles.link}
                                        icon={{
                                            iconProps: { src: "KeyboardArrowDown", size: 8, color: "common.white" },
                                            position: "right",
                                        }}
                                    >
                                        {link.title}
                                    </Link>
                                );
                                return (
                                    <StyledWrapper
                                        // eslint-disable-next-line react/no-array-index-key
                                        key={`${link.url}.${link.title}${index}`}
                                        {...styles.linkWrapper}
                                        className="header-locations-link"
                                    >
                                        <Tooltip
                                            interactiveDebounce={200}
                                            {...styles.postalTippy}
                                            appendTo="parent"
                                            triggerComponent={trigger}
                                            interactive={true}
                                            trigger="focus mouseenter manually"
                                            passAsChildren
                                        >
                                            <PostalCode fulfillmentMethod={session.fulfillmentMethod} />
                                        </Tooltip>
                                    </StyledWrapper>
                                );
                            }

                            const image = (fields.links[index].fields as any).image;
                            return (
                                // eslint-disable-next-line react/no-array-index-key
                                <StyledWrapper {...styles.linkWrapper} key={`${link.url}.${link.title}${index}`}>
                                    {image && (
                                        <LazyImage
                                            src={image}
                                            altText={fields.links[index].fields.overriddenTitle || link.title}
                                            {...styles.logoMark}
                                        />
                                    )}
                                    <Link
                                        href={link.url}
                                        target={fields.links[index].fields.openInNewWindow ? "_blank" : ""}
                                        {...styles.link}
                                    >
                                        {fields.links[index].fields.overriddenTitle || link.title}
                                    </Link>
                                </StyledWrapper>
                            );
                        }
                    },
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(HeaderLinkList),
    definition: {
        group: "Header",
        icon: "list-ul",
        fieldDefinitions: [
            {
                name: fields.showPostalPOpup,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.links,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { type, value } = item.fields.destination;
                    const { overrideTitle, title } = item.fields;
                    if (type === "Page") {
                        const link = getPageLinkByNodeId(state, value);
                        return overrideTitle || link?.title;
                    }
                    if (type === "Category") {
                        if (overrideTitle) {
                            return overrideTitle;
                        }
                        if (value === emptyGuid) {
                            return "Products";
                        }
                        const categoryState = getCategoryState(state, value);
                        if (!categoryState.value) {
                            if (!categoryState.isLoading && value) {
                                return dispatch => {
                                    dispatch(
                                        loadCategory({
                                            id: value,
                                        }),
                                    );
                                };
                            }
                            return "";
                        }
                        return categoryState.value.shortDescription;
                    }
                    if (type === "Url") {
                        return title ?? value;
                    }
                    return value;
                },
                defaultValue: [],
                fieldType: "General",
                fieldDefinitions: [
                    {
                        name: "destination",
                        editorTemplate: "LinkField",
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        isRequired: true,
                    },
                    {
                        name: "overriddenTitle",
                        editorTemplate: "TextField",
                        defaultValue: "",
                        isRequired: true,
                    },
                    {
                        name: "openInNewWindow",
                        editorTemplate: "CheckboxField",
                        defaultValue: false,
                    },
                    {
                        name: "image",
                        editorTemplate: "ImagePickerField",
                        defaultValue: "",
                        isRequired: false,
                    },
                ],
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
